import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { publicCmsAPI } from '../../lib/cmsApi';
import {
  ArrowRight,
  Loader2,
  Mail,
  Linkedin,
  User,
} from 'lucide-react';

const TeamPage = () => {
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await publicCmsAPI.getTeam();
        setTeam(res.data || []);
      } catch (error) {
        console.error('Error fetching team:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div data-testid="team-page">
      {/* Hero Section */}
      <section className="bg-[#1D1D1B] py-20 lg:py-24">
        <div className="container-app">
          <div className="max-w-3xl">
            <p className="text-[#FFD537] font-semibold mb-4 animate-fadeIn">NOTRE EQUIPE</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6 animate-fadeIn">
              Des experts passionnes
            </h1>
            <p className="text-xl text-white/70 leading-relaxed animate-fadeIn animation-delay-100">
              Notre equipe multidisciplinaire reunit des ingenieurs, hydrologues et specialistes en environnement
              devoues a concevoir des solutions durables pour la gestion des eaux et des infrastructures.
            </p>
          </div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="section-padding bg-white">
        <div className="container-app">
          {team.length === 0 ? (
            <div className="text-center py-16">
              <User className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500 text-lg">L'equipe sera bientot presentee ici.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <Card
                  key={member.id}
                  className="overflow-hidden group hover:shadow-xl transition-all duration-300 animate-fadeIn"
                  style={{ animationDelay: `${index * 80}ms` }}
                  data-testid={`team-member-${member.slug}`}
                >
                  <div className="aspect-[4/5] bg-neutral-100 relative overflow-hidden">
                    {member.photo_url ? (
                      <img
                        src={member.photo_url}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-200">
                        <User className="w-24 h-24 text-neutral-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1D1D1B]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-4 left-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-[#F9B438] transition-colors"
                          data-testid={`team-email-${member.slug}`}
                        >
                          <Mail className="w-4 h-4 text-white" />
                        </a>
                      )}
                      {member.linkedin && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-[#0077B5] transition-colors"
                          data-testid={`team-linkedin-${member.slug}`}
                        >
                          <Linkedin className="w-4 h-4 text-white" />
                        </a>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-heading font-semibold text-lg text-[#1D1D1B] mb-1">
                      {member.name}
                    </h3>
                    <p className="text-[#F9B438] font-medium text-sm mb-3">{member.title}</p>
                    {member.bio && (
                      <p className="text-neutral-600 text-sm line-clamp-3 mb-3">{member.bio}</p>
                    )}
                    {member.expertise_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {member.expertise_tags.map((tag, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="text-xs border-[#F9B438]/30 text-[#1D1D1B]"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Values Section */}
      <section
        className="py-16"
        style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.08) 0%, rgba(249,180,56,0.08) 100%)' }}
      >
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-4">
              Nos valeurs
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Chaque membre de notre equipe partage une vision commune : innover pour un avenir durable.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Excellence technique',
                desc: "Nous maintenons les plus hauts standards de qualite dans chacune de nos interventions.",
              },
              {
                title: 'Innovation durable',
                desc: "Nous developpons des solutions qui respectent l'environnement et repondent aux defis d'aujourd'hui.",
              },
              {
                title: 'Collaboration',
                desc: 'Nous croyons au travail collaboratif avec nos clients, partenaires et communautes.',
              },
            ].map((value, i) => (
              <div key={i} className="text-center">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
                >
                  <span className="text-[#1D1D1B] font-bold text-xl">{i + 1}</span>
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#1D1D1B] mb-2">{value.title}</h3>
                <p className="text-neutral-600 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1D1D1B] py-16">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Rejoignez notre equipe
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Nous sommes toujours a la recherche de talents passionnes par l'ingenierie et le developpement durable.
          </p>
          <Link to="/contact">
            <Button size="lg" data-testid="team-cta-btn">
              Contactez-nous
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
